import {observable, action, makeObservable, computed} from 'mobx'
import {type GridReadyEvent, GridApi, RowNode, IRowDragItem, RowDragCallbackParams, IRowNode} from 'ag-grid-community'
import {
    type OcTreeItem,
    OcTreeDragPosition,
    ocTreeItemWalkUp,
    ocTreeItemWalkDown,
    OcTreeItemBasicProps
} from './OcTreeUtil'
export abstract class OcTreeStateBase<T extends OcTreeItemBasicProps<T>> {
    public static readonly EXCLUDED_GRID_ROW_ID = 999999
    public static readonly ERROR_SEARCH_VALUE = '~~**##validation-error##**~~'
    @observable public itemList: OcTreeItem<T>[] = []
    @observable public gridLoading = true
    @observable public quickFilter = ''
    @observable public dragedOverNode?: RowNode = undefined
    @observable public dragedOverPosition?: OcTreeDragPosition = undefined
    @observable public selectedItemId?: string = undefined
    @observable public gridInited = false

    protected gridApi?: GridApi = undefined

    constructor(public readonly highlightParents: boolean) {
        this.getProperty = this.getProperty.bind(this)
        this.getQuickFilterSearchValueForItem = this.getQuickFilterSearchValueForItem.bind(this)
        this.isAnyChildSelected = this.isAnyChildSelected.bind(this)
        this.getParentCount = this.getParentCount.bind(this)
        this.isDragedOver = this.isDragedOver.bind(this)
        this.hasAnyChildError = this.hasAnyChildError.bind(this)
        makeObservable(this)
    }

    /**
     * Method for item sorting - used to sort items as tree.
     * @param itemA first item for sort
     * @param itemB second item for sort
     * @param parent parent of sorted items
     */
    public abstract sortItemsForParent(itemA: T, itemB: T, parent?: T): number
    /**
     * Method to read properties from item.data.
     * @param propertyName property name
     * @param item item, where the property should be found
     */
    public abstract getProperty(
        propertyName: string,
        item?: T
    ): string | number | boolean | string[] | number[] | Date | undefined
    /**
     * Returns empty item.data, to use it as virtual item for search results
     * @param searchValue value used for searching
     * @param count count of filtered items
     * @returns item.data
     */
    public abstract getVirtualItemForGridBase(searchValue: string, count: number): T
    /**
     * Updates virtual item, to display new search results
     * @param searchValue value used for searching
     * @param count count of filtered items
     * @param virtualItem virtual item to update
     */
    public abstract updateVirtualItem(searchValue: string, count: number, virtualItem: OcTreeItem<T>): void
    /**
     * Counts drag target position
     * @param dragNode RowNode beeing dragged
     * @param newOverNode RowNode that is the possible target.
     * @param position counted position from top of row
     * @returns OcTreeDragPosition - before/after/child
     */
    public abstract countDragPosition(
        dragNode: IRowNode,
        newOverNode: IRowNode,
        position: number
    ): OcTreeDragPosition | undefined
    /**
     * Gets the display text for drag item
     * @param params IRowDragItem params from ag grid
     * @returns text for the drag item
     */
    public abstract getRowDragText(params: IRowDragItem): string
    /**
     * Gets the display text for drag item
     * @param params RowDragCallbackParams params from ag grid
     * @returns true/false if row should be dragable
     */
    public abstract rowDragCheck(params: RowDragCallbackParams): boolean
    /**
     * Execute any needed post drop actions
     * @param dropItem item beeing dragged
     * @param dragedOverItem Item that is the drop target - can be sibling
     * @param position Enum OcTreeDragPosition - before/after/child
     * @param newParent Item that is new parent
     * @param oldParent Item that is old parent
     */
    public abstract postDropSortActions(
        dropItem: OcTreeItem<T>,
        dragedOverItem: OcTreeItem<T>,
        position: OcTreeDragPosition,
        newParent?: OcTreeItem<T>,
        oldParent?: OcTreeItem<T>
    ): void
    /**
     * Converts data to OcTreeItem container
     * @param item data to wrap
     * @param expanded default value for expanded
     * @param visibleInGrid default value for visibleInGrid
     * @returns OcTreeItem<T> container
     */
    protected abstract itemToContainer(item: T, expanded?: boolean, visibleInGrid?: boolean): OcTreeItem<T>

    @computed
    public get selectedItem(): OcTreeItem<T> | undefined {
        return this.itemList.find(item => item.data.id === this.selectedItemId)
    }

    @action.bound
    public initGrid(event: GridReadyEvent): void {
        this.gridApi = event.api
        this.gridApi?.sizeColumnsToFit()

        this.gridInited = true
    }

    @action.bound
    public setItemList(rootItems: T[]) {
        this.itemList = []

        const rootContainers = rootItems
            .map(item => this.itemToContainer(item))
            .sort((a: OcTreeItem<T>, b: OcTreeItem<T>) => this.sortItemsForParent(a.data, b.data, undefined))

        // addMode=true - adds items to itemList
        this.updateTreeDataRowIds(rootContainers, true)
        this.gridApi?.setGridOption('rowData', this.itemList)
        this.gridApi?.redrawRows()
    }

    @action.bound
    public refreshCells(force = false) {
        this.gridApi?.refreshCells({force: force})
    }

    @action.bound
    public redrawRows(itemId: string[]) {
        const nodes: RowNode[] = []
        itemId.forEach(id => {
            const node = this.gridApi?.getRowNode(id)
            if (node) {
                nodes.push(node as RowNode)
            }
        })
        this.gridApi?.redrawRows({rowNodes: nodes})
    }

    @action.bound
    public updateRows(itemId: string[]) {
        const dataToUpdate: OcTreeItem<T>[] = []
        itemId.forEach(id => {
            const node = this.gridApi?.getRowNode(id)
            if (node) {
                dataToUpdate.push(node.data as OcTreeItem<T>)
            }
        })
        this.gridApi?.applyTransaction({update: dataToUpdate})
    }

    @action.bound
    public sizeToFit(): void {
        this.gridApi?.sizeColumnsToFit()
    }

    @action.bound
    public setSelectedItemId(itemId?: string): void {
        this.selectedItemId = itemId
        this.setSelectedNode(itemId)
    }

    @action.bound
    public setSelectedNode(itemId?: string): void {
        if (itemId) {
            const rowNode = this.gridApi?.getRowNode(itemId)
            if (rowNode) {
                rowNode.setSelected(true)
                this.selectedItemId = (rowNode.data as OcTreeItem<T>).data.id
                this.itemWalkUp(
                    rowNode.data as OcTreeItem<T>,
                    e => {
                        const typedItem = e as OcTreeItem<T>
                        typedItem.expanded = true
                        typedItem.visibleInGrid = true
                        typedItem.children.forEach(child => {
                            child.visibleInGrid = true
                        })
                        return true
                    },
                    true
                )
                this.gridApi?.onFilterChanged()
                this.gridApi?.ensureNodeVisible(rowNode)
            }
        } else {
            const rowNode = this.gridApi?.getRowNode(this.selectedItemId || '')
            if (rowNode) {
                rowNode.setSelected(false)
                this.selectedItemId = undefined
            }
        }
    }

    @action.bound
    public setSelectedPostAction(newItem: OcTreeItem<T>): void {
        const oldSelectedId = this.selectedItemId
        this.selectedItemId = newItem.data.id
        if (this.highlightParents) {
            const oldItem = this.itemList.find(item => item.data.id === oldSelectedId)
            const itemIds = new Set<string>()
            if (oldItem) {
                this.itemWalkUp(oldItem, item => {
                    const typedItem = item as OcTreeItem<T>
                    itemIds.add(typedItem.data.id)
                    return true
                })
            }
            if (newItem) {
                this.itemWalkUp(newItem, item => {
                    const typedItem = item as OcTreeItem<T>
                    itemIds.add(typedItem.data.id)
                    return true
                })
            }
            this.updateRows(Array.from(itemIds))
        }
    }

    @computed
    public get dragPosition(): OcTreeDragPosition | undefined {
        return this.dragedOverPosition
    }

    public parsePropertyToSearchString(
        property: string | number | boolean | string[] | number[] | Date | undefined,
        propertyName: string
    ): string {
        if (property) {
            if ((property as any).length && (property as any).push && (property as any).push) {
                return (property as any).map((item: string | number) => `${item.valueOf()}`).join(',')
            } else if (typeof property === 'boolean') {
                return propertyName
            } else {
                return `${property.valueOf()}`
            }
        }
        return ''
    }

    public getQuickFilterSearchValueForItem(itemId: string, property: string, includeParent = false): string {
        const rowItem = this.itemList.find(item => item.data.id === itemId)
        const value = this.getProperty(property, rowItem?.data)
        let result = this.parsePropertyToSearchString(value, property)

        if (rowItem && includeParent) {
            rowItem.children.forEach(child =>
                this.itemWalkDown(child, item => {
                    const typedItem = item as OcTreeItem<T>
                    const parentValue = this.getProperty(property, typedItem.data)
                    result = `${result}~${this.parsePropertyToSearchString(parentValue, property)}`

                    return true
                })
            )
        }

        return result.toLowerCase()
    }

    public isAnyChildSelected(itemId: string): boolean {
        let result = false
        if (this.selectedItemId !== itemId) {
            const rowItem = this.itemList.find(item => item.data.id === itemId)
            if (rowItem) {
                if (rowItem.children.find(child => child.data.id === this.selectedItemId)) {
                    result = true
                }
                rowItem.children.forEach(child =>
                    this.itemWalkDown(child, item => {
                        const typedItem = item as OcTreeItem<T>
                        if (typedItem.data.id === this.selectedItemId) {
                            result = true
                            return false
                        }
                        return true
                    })
                )
            }
        }

        return result
    }

    public hasAnyChildError(itemId: string): boolean {
        let result = false
        const treeItem = this.itemList.find(item => item.data.id === itemId)
        if (treeItem) {
            this.itemWalkDown(
                treeItem,
                item => {
                    const typedItem = item as OcTreeItem<T>
                    if (typedItem.defaultSearchField === OcTreeStateBase.ERROR_SEARCH_VALUE) {
                        result = true
                        return false
                    }
                    return true
                },
                true
            )
        }

        return result
    }

    public getParentCount(itemId: string): number {
        const base = this.itemList.find(item => item.data.id === itemId)

        if (base) {
            let count = 0
            let currentItem: OcTreeItem<T> | undefined = base
            const findItem = (item: OcTreeItem<T>) => item.data.id === currentItem?.data.parentId
            while (currentItem?.data.parentId) {
                count++
                currentItem = this.itemList.find(findItem)
            }
            return count
        }
        return 0
    }

    @computed
    public get isQuickFilterActive(): boolean {
        return this.quickFilter !== ''
    }

    public getVirtualItemForGrid(searchString: string, count: number): OcTreeItem<T> {
        const data = this.getVirtualItemForGridBase(searchString, count)
        data.id = OcTreeStateBase.EXCLUDED_GRID_ROW_ID.toString()
        return {
            data,
            children: [],
            expanded: false,
            visibleInGrid: true,
            gridRowId: OcTreeStateBase.EXCLUDED_GRID_ROW_ID,
            defaultSearchField: searchString
        }
    }

    @action.bound
    public setOcQuickFilter(value: string): void {
        this.quickFilter = value.toLowerCase()
        this.gridApi?.setGridOption('quickFilterText', value.toLowerCase())
        const selected = this.itemList.find(item => item.data.id === this.selectedItemId)

        let virtualRow = this.gridApi?.getRowNode(`${OcTreeStateBase.EXCLUDED_GRID_ROW_ID}`)?.data as OcTreeItem<T>
        if (!value) {
            if (virtualRow) {
                this.gridApi?.applyTransaction({remove: [virtualRow]})
            }
            if (selected) {
                this.itemWalkUp(
                    selected,
                    parent => {
                        const typedParent = parent as OcTreeItem<T>
                        this.toggleParentAndDisplayChildren(typedParent.data.id, true, false)
                        return true
                    },
                    true
                )
            }
        } else {
            let count = this.gridApi?.getDisplayedRowCount() || 0
            if (virtualRow) {
                const lastNode = this.gridApi?.getDisplayedRowAtIndex(this.gridApi?.getLastDisplayedRowIndex())
                if (lastNode && lastNode.data.id === `${OcTreeStateBase.EXCLUDED_GRID_ROW_ID}`) {
                    count = count - 1
                }

                this.updateVirtualItem(value, count, virtualRow)
                virtualRow.defaultSearchField = value
            } else {
                virtualRow = this.getVirtualItemForGrid(value, count)
                this.gridApi?.applyTransaction({add: [virtualRow]})
            }
            this.updateRows([virtualRow.data.id])
            this.gridApi?.getRowNode(`${OcTreeStateBase.EXCLUDED_GRID_ROW_ID}`)?.resetQuickFilterAggregateText()
        }

        this.gridApi?.onFilterChanged()
        this.ensureSelectedVisible()
    }

    @action.bound
    public expandAll(): void {
        this.itemList
            .filter(item => !item.data.parentId && item.gridRowId !== OcTreeStateBase.EXCLUDED_GRID_ROW_ID)
            .forEach(item => {
                this.toggleParentAndDisplayChildren(item.data.id, true, true)
            })

        this.sizeToFit()
        this.ensureSelectedVisible()
    }

    @action.bound
    public collapseAll(): void {
        this.itemList
            .filter(item => !item.data.parentId && item.gridRowId !== OcTreeStateBase.EXCLUDED_GRID_ROW_ID)
            .forEach(item => {
                this.toggleParentAndDisplayChildren(item.data.id, false, true)
            })

        this.sizeToFit()
        this.ensureSelectedVisible()
    }

    @action.bound
    public ensureSelectedVisible(): void {
        const rowNode = this.gridApi?.getRowNode(this.selectedItemId || '')
        this.gridApi?.ensureNodeVisible(rowNode)
    }

    /**
     * Switch item to expanded/collaped based on the value and hide/display children. Children can be expanded too.
     * @param itemId Item to expand
     * @param value new value for expanded property true/false
     * @param updateExpandedState true/false, update expanded state of children too, false is default
     */
    @action.bound
    public toggleParentAndDisplayChildren(itemId: string, value: boolean, updateExpandedState = false): void {
        const parent = this.itemList.find(item => item.data.id === itemId)

        if (parent) {
            parent.expanded = value
            parent.children.forEach(child =>
                this.itemWalkDown(child, item => {
                    const typedItem = item as OcTreeItem<T>
                    typedItem.visibleInGrid = value
                    if (updateExpandedState) {
                        typedItem.expanded = value
                    }
                    if (value && !typedItem.expanded) {
                        return false
                    }
                    return true
                })
            )

            this.refreshCells()
            this.gridApi?.onFilterChanged()
        }
    }

    /**
     * Walks down the item children in prefix order.
     * @param start Item from which to start
     * @param eqAction Action executed on each item in subtree. Return true/false to say if you want to continue with children.
     * @param sortFunction Sort function for children, can be skipped in case order does not matter
     * @param skipFirst Skip the start item from the walk
     */
    @action.bound
    public itemWalkDown(start: OcTreeItem<T>, eqAction: (e: OcTreeItem<T>) => boolean, skipFirst = false): void {
        ocTreeItemWalkDown(start, eqAction as (e: OcTreeItem<T> | T) => boolean, this.sortItemsForParent, skipFirst)
    }

    /**
     * Walks up the item parents.
     * @param start Item from which to start
     * @param eqAction Action executed on each parent. Return true/false to say if you want to continue with parent.
     * @param skipFirst Skip the start item from the walk
     */
    @action.bound
    public itemWalkUp(start: OcTreeItem<T>, eqAction: (e: OcTreeItem<T>) => boolean, skipFirst = false): void {
        ocTreeItemWalkUp(this.itemList, start, eqAction as (e: OcTreeItem<T> | T) => boolean, skipFirst)
    }

    public isDragedOver(itemId: string): boolean {
        if (this.dragedOverNode && (this.dragedOverNode.data as OcTreeItem<T>).data.id === itemId) {
            return true
        }
        return false
    }

    /**
     * Check if newOverNode can be drag&drop target and if yes, counts the position and cann ro redraw.
     * @param newOverNode RowNode that is the possible target.
     * @param dragNode RowNode beeing dragged
     * @param lengthFromTop Length from from top of the newOverNode to count if it should be before/after/child
     */
    @action.bound
    public setDragedOverNode(
        newOverNode: IRowNode | undefined,
        dragNode: IRowNode | undefined,
        lengthFromTop: number
    ): void {
        const current = this.dragedOverNode
        const rowsToRedraw: string[] = []

        if (!newOverNode) {
            rowsToRedraw.push((this.dragedOverNode?.data as OcTreeItem<T>)?.data.id || '')
            this.dragedOverNode = undefined
            this.dragedOverPosition = undefined
        } else {
            const position = (lengthFromTop - (newOverNode.rowTop || 0)) / (newOverNode.rowHeight || 0)

            if (dragNode) {
                if ((newOverNode.data as OcTreeItem<T>).data.id !== (current?.data as OcTreeItem<T>)?.data.id) {
                    if ((newOverNode.data as OcTreeItem<T>).data.id !== (dragNode.data as OcTreeItem<T>).data.id) {
                        this.dragedOverNode = newOverNode as RowNode
                        this.dragedOverPosition = this.countDragPosition(dragNode, newOverNode, position)
                        rowsToRedraw.push((newOverNode.data as OcTreeItem<T>).data.id)
                        rowsToRedraw.push((current?.data as OcTreeItem<T>)?.data.id || '')
                    } else {
                        rowsToRedraw.push((this.dragedOverNode?.data as OcTreeItem<T>)?.data.id || '')
                        this.dragedOverNode = undefined
                        this.dragedOverPosition = undefined
                    }
                } else {
                    const newDragPositon = this.countDragPosition(dragNode, newOverNode, position)

                    if (newDragPositon !== this.dragedOverPosition) {
                        this.dragedOverPosition = newDragPositon
                        rowsToRedraw.push((newOverNode.data as OcTreeItem<T>).data.id || '')
                    }
                }
            }
        }

        this.updateRows(Array.from(new Set(rowsToRedraw)))
    }

    /**
     * Check if the drop target is new parent or the old one, and execute postDropSortActions method.
     * @param item Item beeing dragged
     */
    @action.bound
    public finishDragAndDrop(item: OcTreeItem<T>): void {
        const outdatedRows = []
        if (this.dragedOverNode && this.dragedOverPosition) {
            const origParent = item.data.parentId
                ? this.itemList.find(parent => parent.data.id === item.data.parentId)
                : undefined
            let newParent = undefined
            if (this.dragedOverPosition === OcTreeDragPosition.In) {
                newParent = this.itemList.find(
                    parent => parent.data.id === (this.dragedOverNode?.data as OcTreeItem<T>).data.id
                )
            } else {
                newParent = this.itemList.find(
                    parent => parent.data.id === (this.dragedOverNode?.data as OcTreeItem<T>).data.parentId
                )
            }

            if (origParent?.data.id === newParent?.data.id) {
                this.postDropSortActions(
                    item,
                    this.dragedOverNode.data as OcTreeItem<T>,
                    this.dragedOverPosition,
                    newParent,
                    origParent
                )
            } else {
                if (origParent) {
                    const childItem = origParent.children.find(child => child.data.id === item.data.id)
                    if (childItem) {
                        const childIdx = origParent.children.indexOf(childItem)
                        origParent.children.splice(childIdx, 1)

                        const dataIdx = origParent.data.children.indexOf(childItem.data)
                        origParent.data.children.splice(dataIdx, 1)
                        origParent.data.highlightChange = true
                        outdatedRows.push(origParent.data.id)
                    }
                }

                if (newParent) {
                    newParent.children.push(item)
                    newParent.data.children.push(item.data)
                    item.data.parentId = newParent.data.id
                    item.data.hasParentChanged = true
                    this.postDropSortActions(
                        item,
                        this.dragedOverNode.data as OcTreeItem<T>,
                        this.dragedOverPosition,
                        newParent,
                        origParent
                    )
                    this.toggleParentAndDisplayChildren(newParent.data.id, true)
                    outdatedRows.push(newParent.data.id)
                    outdatedRows.push(item.data.id)
                }
            }
        }
        const rootEq = this.itemList.filter(item => !item.data.parentId)
        this.updateTreeDataRowIds(rootEq)
        this.gridApi?.onFilterChanged()
        this.gridApi?.onSortChanged()
        this.redrawRows(outdatedRows)
    }

    /**
     * Updates the items to refresh tree structure, needs to be done, when some items changes location in tree.
     * Updates gridRowId for every item and also adjust if it should be expanded and visible based on parent.
     * @param rootEquipment Root items(without parent) to do the walkdown
     * @param addMode optional param - if true items will be added to itemList while walkdown
     */
    @action.bound
    public updateTreeDataRowIds(rootEquipment: OcTreeItem<T>[], addMode?: boolean): void {
        let idx = 1

        rootEquipment.forEach(rootEq => {
            this.itemWalkDown(rootEq, e => {
                const typedItem = e as OcTreeItem<T>
                typedItem.gridRowId = idx

                if (typedItem.data.parentId) {
                    const parent = this.itemList.find(eq => eq.data.id === typedItem.data.parentId)
                    typedItem.visibleInGrid = parent && parent.expanded && (parent.visibleInGrid || false)
                } else {
                    typedItem.visibleInGrid = true
                }

                idx = idx + 1
                if (addMode) {
                    this.itemList.push(typedItem)
                }

                return true
            })
        })
    }
}
